/**
 * Learn / Improve section.
 *
 * A guided overview of every solving strategy the engine teaches, grouped by
 * difficulty tier. Each technique gets a plain-language "what & when/why"
 * write-up plus an **interactive demonstration**: a real, verified board
 * position (mined from actual solve traces) that you step through to see the
 * pattern, then the elimination or placement it produces.
 */

import lessonsData from "../data/lessons.json";
import { UNITS, hasDigit, rowOf, colOf } from "../core/board.js";
import { el, on } from "./dom.js";
import { icons } from "./icons.js";

interface LessonExample {
  technique: string;
  name: string;
  tier: number;
  values: number[];
  candidates: number[];
  focusCells: number[];
  units: number[];
  digits: number[];
  placements: { cell: number; digit: number }[];
  eliminations: { cell: number; digit: number }[];
  summary: string;
  explanation: string;
}

const LESSONS = lessonsData as Record<string, LessonExample>;

interface TechInfo {
  id: string;
  name: string;
  tier: number;
  overview: string;
}

const TIER_LABEL: Record<number, string> = {
  1: "Basics",
  2: "Intermediate",
  3: "Advanced",
  4: "Expert",
  5: "Master",
};
const TIER_COLOR: Record<number, string> = {
  1: "var(--easy)",
  2: "var(--medium)",
  3: "var(--hard)",
  4: "var(--expert)",
  5: "var(--insane)",
};

const TECHNIQUES: TechInfo[] = [
  { id: "fullHouse", name: "Full House", tier: 1, overview: "The simplest move of all: a row, column or box with a single empty cell left. Only one digit is missing, so it must go there. Always sweep for these first, finishing a house often triggers a chain of further singles." },
  { id: "nakedSingle", name: "Naked Single", tier: 1, overview: "When a cell has only one candidate left, every other digit already appears in its row, column or box, that digit must go there. It's the very first thing to scan for, and filling one often unlocks the next." },
  { id: "hiddenSingle", name: "Hidden Single", tier: 1, overview: "Within a single row, column or box, find a digit that can legally land in only one cell. Even if that cell shows several candidates, the digit has nowhere else to go. Hidden singles are the workhorse of easy and medium puzzles." },
  { id: "pointing", name: "Pointing Pair/Triple", tier: 2, overview: "When a digit's candidates inside a box all sit on one row or column, the digit must end up along that line. You can then erase it from the rest of that row/column outside the box, frequently exposing a single." },
  { id: "claiming", name: "Box/Line Reduction", tier: 2, overview: "The mirror of pointing: when a digit in a row or column is confined to a single box, it must be placed inside that overlap, so you can remove it from the box's other cells. Use it to tidy candidates before hunting subsets." },
  { id: "nakedPair", name: "Naked Pair", tier: 2, overview: "Two cells in a unit that share exactly the same two candidates lock those digits between them. Neither digit can appear elsewhere in that unit, so cross both out of the other cells." },
  { id: "hiddenPair", name: "Hidden Pair", tier: 2, overview: "Two digits that can only go in the same two cells of a unit reserve those cells, even if the cells show extra candidates. Strip the extras away to reveal the pair." },
  { id: "nakedTriple", name: "Naked Triple", tier: 3, overview: "Three cells whose candidates collectively use only three digits confine those digits to those cells, so they can be removed from the rest of the unit. The three cells needn't each show all three digits." },
  { id: "hiddenTriple", name: "Hidden Triple", tier: 3, overview: "Three digits restricted to the same three cells of a unit can be locked in, clearing every other candidate from those cells. Harder to spot than a naked triple but just as decisive." },
  { id: "nakedQuad", name: "Naked Quad", tier: 3, overview: "Four cells covering only four candidates between them confine those digits, so they can be removed elsewhere in the unit. Rare, but powerful when the candidate grid is dense." },
  { id: "hiddenQuad", name: "Hidden Quad", tier: 3, overview: "Four digits that fit only within four cells of a unit reserve them, clearing all other candidates from those cells. Among the toughest patterns to see by eye, let the pencil marks guide you." },
  { id: "xWing", name: "X-Wing", tier: 3, overview: "Pick a digit. If it appears in exactly two cells on each of two rows, and those cells share the same two columns, it forms an X-Wing. The digit takes opposite corners, so you can remove it from those columns elsewhere (and the same works with rows and columns swapped)." },
  { id: "swordfish", name: "Swordfish", tier: 4, overview: "An X-Wing scaled up to three lines. When a digit's candidates across three rows fit within three columns, it's locked into that 3×3 frame and can be eliminated from those columns everywhere else." },
  { id: "xyWing", name: "XY-Wing", tier: 4, overview: "A pivot cell holds two candidates (X,Y); two wing cells hold (X,Z) and (Y,Z). Whatever the pivot becomes, one wing is forced to Z, so any cell that sees both wings cannot be Z." },
  { id: "xyzWing", name: "XYZ-Wing", tier: 4, overview: "Like an XY-Wing, but the pivot holds all three candidates (X,Y,Z). One of the three cells must be Z, so any cell that sees the pivot and both wings loses Z." },
  { id: "skyscraper", name: "Skyscraper", tier: 4, overview: "A single-digit pattern: two lines in which the digit appears only twice, sharing one 'base' line. The two far cells form a 'roof', whichever is true, any cell that sees BOTH roof ends cannot hold the digit. A clean first step beyond fish." },
  { id: "twoStringKite", name: "Two-String Kite", tier: 4, overview: "Two strong links, one along a row, one along a column, joined where two of their ends share a box. The two far ends together cover one cell (their row/column intersection), which therefore can't hold the digit." },
  { id: "wWing", name: "W-Wing", tier: 4, overview: "Two cells with the same two candidates {x,y}, joined by a strong link on one of those digits. The pair guarantees one of them takes the linked digit, so any cell seeing both loses the OTHER digit." },
  { id: "uniqueRectangle", name: "Unique Rectangle", tier: 4, overview: "Uses the fact that a proper puzzle has exactly one solution. If three corners of a rectangle spanning two boxes share the same two candidates, the fourth corner can't also be just those two (that would allow two solutions), so remove them from it." },
  { id: "uniqueRectangleType2", name: "Unique Rectangle (Type 2)", tier: 4, overview: "A rectangle in two boxes where two corners are a bare pair {x,y} and the other two are {x,y,z}. To dodge the deadly two-solution pattern, one of those two must be z, so any cell seeing both loses z." },
  { id: "uniqueRectangleType4", name: "Unique Rectangle (Type 4)", tier: 4, overview: "Again a {x,y} rectangle, but here one of the two digits is locked to the two roof cells within a house (a strong link). Placing the other digit in a roof would complete the deadly pattern, so that other digit is removed from both roofs." },
  { id: "remotePairs", name: "Remote Pairs", tier: 4, overview: "A chain of cells that all hold the same two candidates {x,y}, each seeing the next. The chain forces the digits to alternate, so any outside cell that can see both an 'x end' and a 'y end' can hold neither, remove both." },
  { id: "emptyRectangle", name: "Empty Rectangle", tier: 4, overview: "When a digit's candidates in a box all share a single row and column (forming an L with an empty rectangle), they combine with a conjugate pair in a line to eliminate the digit from the cell where the row and the link meet." },
  { id: "finnedXWing", name: "Finned X-Wing", tier: 4, overview: "An X-Wing with one extra candidate, the 'fin', in a base unit. The eliminations survive only where they also share a box with the fin, letting you use 'almost' fish that aren't quite perfect." },
  { id: "hiddenRectangle", name: "Hidden Rectangle", tier: 4, overview: "A uniqueness pattern. One corner of a two-box rectangle is a bare pair {x,y}; in the opposite corner's row and column, one of those digits can land only at the rectangle's corners. Placing the other digit there would create two solutions, so it's removed." },
  { id: "wxyzWing", name: "WXYZ-Wing", tier: 4, overview: "Four cells holding only four digits between them, built from a three-cell 'almost locked set' joined to a bivalue cell by a restricted digit. Because that shared digit fits in only one spot, one of the four cells must take the wing digit, so any cell seeing all of its copies loses it. A gentle first step into almost-locked-set logic." },
  { id: "simpleColoring", name: "Simple coloring", tier: 4, overview: "Pick a digit and follow its conjugate pairs (units where it fits in only two cells), coloring them alternately. If two same-colored cells share a unit, that color is false; if a cell sees both colors, it can't hold the digit. Also called Singles Chains." },
  { id: "jellyfish", name: "Jellyfish", tier: 5, overview: "A fish on four lines: when a digit across four rows is confined to four columns (or vice-versa), it forms a Jellyfish and can be eliminated from those columns elsewhere. The largest of the basic fish, reserved for the nastiest puzzles." },
  { id: "finnedSwordfish", name: "Finned Swordfish", tier: 5, overview: "A Swordfish (the three-line fish) spoiled by a fin, one or more extra candidates tucked into a single box. As with the finned X-Wing, only the eliminations that also see the fin survive, but that's often enough to crack the grid." },
  { id: "xyChain", name: "XY-Chain", tier: 5, overview: "A chain of bivalue cells where each shares a digit with the next. When both ends of the chain can 'see' the same digit, any cell that sees both ends can't be that digit, the backbone of many hard eliminations." },
  { id: "xChain", name: "X-Chain", tier: 5, overview: "Follow a single digit along alternating strong links (a house where it fits in just two cells) and weak links (a shared house). A long enough chain makes its two ends act as one strong link, so any cell seeing both ends loses the digit." },
  { id: "alsXZ", name: "ALS-XZ", tier: 5, overview: "The general almost-locked-set move. Two ALSs share a 'restricted common' digit that can occupy only one of them; whichever set misses out becomes locked and must place a second shared digit. That digit is therefore guaranteed in one set or the other, so any cell seeing all its copies in both sets can be cleared of it." },
  { id: "bug", name: "BUG + 1", tier: 5, overview: "Bi-value Universal Grave: if every unsolved cell but one has exactly two candidates, the lone three-candidate cell must take the digit that appears three times across its units, otherwise the grid would have more than one solution." },
];

function miniStaticClass(i: number): string {
  const r = rowOf(i);
  const c = colOf(i);
  const cls = ["mc"];
  if (c % 3 === 0) cls.push("box-left");
  if (r % 3 === 0) cls.push("box-top");
  if (c % 3 === 2) cls.push("box-right");
  if (r % 3 === 2) cls.push("box-bottom");
  return cls.join(" ");
}

const cellName = (i: number): string => `R${rowOf(i) + 1}C${colOf(i) + 1}`;

export class LearnView {
  private sheet: HTMLElement;
  private onClose: () => void;
  private practiceTimer: number | null = null;

  constructor(sheet: HTMLElement, onClose: () => void) {
    this.sheet = sheet;
    this.onClose = onClose;
  }

  private clearPracticeTimer(): void {
    if (this.practiceTimer !== null) {
      clearTimeout(this.practiceTimer);
      this.practiceTimer = null;
    }
  }

  /** True when a technique has a Learn entry an in-game hint can link to. */
  static has(id: string): boolean {
    return TECHNIQUES.some((t) => t.id === id);
  }

  /** Open the Learn section. With a technique id, jump straight to its lesson
   *  (used by in-game hints); otherwise show the technique list. */
  open(techniqueId?: string): void {
    const info = techniqueId ? TECHNIQUES.find((t) => t.id === techniqueId) : undefined;
    if (info) this.openLesson(info);
    else this.renderList();
  }

  private head(title: string, back?: () => void): HTMLElement {
    const head = el("div", "sheet-head");
    const left = el("div");
    left.style.display = "flex";
    left.style.alignItems = "center";
    left.style.gap = "8px";
    if (back) {
      const b = el("button", "icon-btn");
      b.innerHTML = icons.back;
      on(b, "click", back);
      left.appendChild(b);
    }
    const h = el("h2");
    h.textContent = title;
    h.style.margin = "0";
    left.appendChild(h);
    const close = el("button", "icon-btn");
    close.innerHTML = icons.close;
    on(close, "click", () => this.onClose());
    head.append(left, close);
    return head;
  }

  private renderList(): void {
    this.clearPracticeTimer();
    this.sheet.innerHTML = "";
    this.sheet.appendChild(this.head("Learn"));
    const sub = el("p", "sub", "Master every strategy the game teaches. Tap a technique for a walkthrough and an interactive example.");
    this.sheet.appendChild(sub);

    const list = el("div", "learn-list");
    for (let tier = 1; tier <= 5; tier++) {
      const group = TECHNIQUES.filter((t) => t.tier === tier);
      if (!group.length) continue;
      const g = el("div", "learn-group");
      const gt = el("div", "gtitle");
      gt.innerHTML = `<span class="pip" style="background:${TIER_COLOR[tier]}"></span>${TIER_LABEL[tier]}`;
      g.appendChild(gt);
      const cards = el("div", "learn-cards");
      for (const t of group) {
        const hasDemo = !!LESSONS[t.id];
        const card = el("button", "learn-card");
        card.innerHTML = `<div class="lc-name">${t.name}</div><div class="lc-sub">${hasDemo ? "Interactive example" : "Overview"}</div>`;
        on(card, "click", () => this.openLesson(t));
        cards.appendChild(card);
      }
      g.appendChild(cards);
      list.appendChild(g);
    }
    this.sheet.appendChild(list);
    this.sheet.scrollTop = 0;
  }

  private openLesson(info: TechInfo): void {
    this.sheet.innerHTML = "";
    this.sheet.appendChild(this.head(info.name, () => this.renderList()));

    const badge = el("span", "tier");
    badge.textContent = `${TIER_LABEL[info.tier]}`;
    badge.style.background = TIER_COLOR[info.tier];
    this.sheet.appendChild(badge);

    const overview = el("p", "explanation");
    overview.style.margin = "12px 0 6px";
    overview.textContent = info.overview;
    this.sheet.appendChild(overview);

    const example = LESSONS[info.id];
    if (!example) {
      const note = el("p", "sub");
      note.textContent = "An interactive example for this rare technique isn't bundled, but the idea above is all you need, it follows the same logic as its smaller siblings.";
      this.sheet.appendChild(note);
      return;
    }

    // Optional interactive tutorial: Watch (passive walkthrough) or Try it.
    const seg = el("div", "seg");
    seg.style.margin = "12px 0";
    const watchBtn = el("button");
    watchBtn.textContent = "Watch";
    const tryBtn = el("button");
    tryBtn.textContent = "Try it yourself";
    seg.append(watchBtn, tryBtn);
    this.sheet.appendChild(seg);
    const content = el("div");
    this.sheet.appendChild(content);

    const idx = TECHNIQUES.indexOf(info);
    const next = idx >= 0 && idx + 1 < TECHNIQUES.length ? TECHNIQUES[idx + 1] : undefined;

    const setMode = (mode: "watch" | "try") => {
      this.clearPracticeTimer();
      watchBtn.classList.toggle("on", mode === "watch");
      tryBtn.classList.toggle("on", mode === "try");
      content.innerHTML = "";
      if (mode === "watch") this.renderWalkthrough(content, example, next);
      else this.renderPractice(content, example, next);
      this.sheet.scrollTop = 0;
    };
    on(watchBtn, "click", () => setMode("watch"));
    on(tryBtn, "click", () => setMode("try"));
    setMode("watch");
  }

  /** Passive step-through demonstration. */
  private renderWalkthrough(host: HTMLElement, example: LessonExample, next?: TechInfo): void {
    const detail = el("div", "lesson-detail");
    const board = el("div", "mini-board");
    const stepText = el("div", "step-text");
    const stepper = el("div", "stepper");
    const prev = el("button", "btn");
    prev.textContent = "Back";
    const dots = el("div", "dots");
    for (let i = 0; i < 3; i++) dots.appendChild(el("span", "d"));
    const nextBtn = el("button", "btn primary");
    nextBtn.textContent = "Next";
    stepper.append(prev, dots, nextBtn);
    detail.append(board, stepText, stepper);
    host.appendChild(detail);

    let stage = 0;
    const stages = [
      "Here's a real position. The small numbers are the remaining candidates in each empty cell.",
      example.summary + " The highlighted cells form the pattern.",
      example.explanation,
    ];
    const draw = () => {
      this.renderMini(board, example, stage);
      stepText.innerHTML = stages[stage];
      [...dots.children].forEach((d, i) => d.classList.toggle("on", i === stage));
      prev.style.visibility = stage === 0 ? "hidden" : "visible";
      nextBtn.textContent = stage < 2 ? "Next" : next ? `Next: ${next.name} →` : "Got it";
    };
    on(prev, "click", () => {
      if (stage > 0) stage--;
      draw();
    });
    on(nextBtn, "click", () => {
      if (stage < 2) {
        stage++;
        draw();
      } else if (next) {
        this.openLesson(next);
      } else {
        this.renderList();
      }
    });
    draw();
  }

  /** Hands-on tutorial: the player performs the deduction on the set scenario. */
  private renderPractice(host: HTMLElement, ex: LessonExample, next?: TechInfo): void {
    const detail = el("div", "lesson-detail");
    const board = el("div", "mini-board practice");
    const prompt = el("div", "step-text");
    const actions = el("div", "stepper");
    const reveal = el("button", "btn");
    const reset = el("button", "btn");
    reset.textContent = "Reset";
    const done = el("button", "btn primary");
    done.textContent = "Done";
    const nextBtn = el("button", "btn primary");
    nextBtn.textContent = next ? `Next: ${next.name} →` : "Done";
    nextBtn.style.display = "none";
    actions.append(reveal, reset, done, nextBtn);
    detail.append(board, prompt, actions);
    host.appendChild(detail);

    const isPlacement = ex.placements.length > 0;
    const target = ex.placements[0];
    const firstElim = ex.eliminations[0];
    const elimWanted = new Set(ex.eliminations.map((e) => e.cell + ":" + e.digit));
    const crossed = new Set<string>();
    let revealed = false;
    let solved = false;
    let advice = 0; // 0 = none, 1 = show the pattern, 2 = point at the move

    const task = isPlacement
      ? `Your turn, tap the candidate that <b>must</b> be placed by this ${ex.name}.`
      : `Your turn, tap every candidate this ${ex.name} lets you <b>eliminate</b> (${ex.eliminations.length} to find).`;

    const adviceText = (): string => {
      if (advice >= 2) {
        if (isPlacement && target)
          return `Stuck? Tap <b>${target.digit}</b> in <b>${cellName(target.cell)}</b>, it's the only place it can go.`;
        if (firstElim)
          return `Stuck? Start by crossing out <b>${firstElim.digit}</b> in <b>${cellName(firstElim.cell)}</b>, it can't go there.`;
      }
      return `Take your time. Here's the pattern, ${ex.summary}`;
    };

    const adviceCells = (): Set<number> | undefined => {
      if (advice < 2) return undefined;
      const c = isPlacement ? target?.cell : firstElim?.cell;
      return c === undefined ? undefined : new Set([c]);
    };

    const scheduleAdvice = () => {
      this.clearPracticeTimer();
      if (solved || advice >= 2) return;
      this.practiceTimer = window.setTimeout(
        () => {
          advice++;
          if (advice >= 1) revealed = true;
          draw();
        },
        advice === 0 ? 12000 : 10000,
      );
    };

    const flashWrong = (span: HTMLElement) => {
      span.classList.add("wrong");
      setTimeout(() => span.classList.remove("wrong"), 360);
    };

    const onCandidate = (cell: number, digit: number, span: HTMLElement) => {
      if (solved) return;
      advice = 0; // the player is trying, reset the nudges
      const k = cell + ":" + digit;
      if (isPlacement) {
        if (target && cell === target.cell && digit === target.digit) {
          solved = true;
          draw();
        } else {
          flashWrong(span);
          prompt.innerHTML = `Not quite, that one isn't forced.${revealed ? "" : " Try <b>Reveal pattern</b> for a clue."}`;
          scheduleAdvice();
        }
      } else if (elimWanted.has(k)) {
        if (crossed.has(k)) crossed.delete(k);
        else crossed.add(k);
        if (crossed.size === elimWanted.size) solved = true;
        draw();
      } else {
        flashWrong(span);
        prompt.innerHTML = "That candidate can still go there, it isn't eliminated by this pattern.";
        scheduleAdvice();
      }
    };

    // A full redraw shows the task (or the success message). Wrong-answer
    // feedback is written straight to the prompt WITHOUT a redraw, so the
    // shake animation on the tapped candidate survives. After a stall, escalating
    // advice highlights the pattern, then points at the exact move.
    const draw = () => {
      this.renderPracticeBoard(board, ex, {
        crossed,
        revealed,
        solved,
        onCandidate,
        adviceCells: adviceCells(),
      });
      const progress =
        !isPlacement && crossed.size > 0
          ? `Good, ${crossed.size} of ${elimWanted.size} found. Keep going.`
          : task;
      prompt.innerHTML = solved
        ? `<b>Nicely done!</b> ${ex.explanation}`
        : advice >= 1
          ? adviceText()
          : progress;
      reveal.textContent = revealed ? "Hide pattern" : "Reveal pattern";
      reveal.style.display = solved ? "none" : "";
      reset.style.display = solved ? "none" : "";
      done.style.display = solved && next ? "none" : "";
      nextBtn.style.display = solved && next ? "" : "none";
      if (solved) this.clearPracticeTimer();
      else scheduleAdvice();
    };

    on(reveal, "click", () => {
      revealed = !revealed;
      draw();
    });
    on(reset, "click", () => {
      crossed.clear();
      solved = false;
      revealed = false;
      advice = 0;
      draw();
    });
    on(done, "click", () => {
      this.clearPracticeTimer();
      this.renderList();
    });
    on(nextBtn, "click", () => {
      this.clearPracticeTimer();
      if (next) this.openLesson(next);
      else this.renderList();
    });
    draw();
  }

  private renderPracticeBoard(
    container: HTMLElement,
    ex: LessonExample,
    opts: {
      crossed: Set<string>;
      revealed: boolean;
      solved: boolean;
      onCandidate: (cell: number, digit: number, span: HTMLElement) => void;
      adviceCells?: Set<number>;
    },
  ): void {
    container.innerHTML = "";
    const focus = new Set(ex.focusCells);
    const unitCells = new Set<number>();
    if (opts.revealed) for (const u of ex.units) for (const c of UNITS[u]) unitCells.add(c);
    const placeAt = new Map<number, number>();
    for (const p of ex.placements) placeAt.set(p.cell, p.digit);

    for (let i = 0; i < 81; i++) {
      const mc = el("div", miniStaticClass(i));
      const value = ex.values[i];
      const placedHere = opts.solved ? placeAt.get(i) : undefined;
      if (opts.revealed && unitCells.has(i) && !focus.has(i)) mc.classList.add("unit");
      if (opts.revealed && focus.has(i)) mc.classList.add("focus");
      if (opts.adviceCells?.has(i)) mc.classList.add("advice");

      if (value) {
        mc.textContent = String(value);
      } else if (placedHere !== undefined) {
        mc.textContent = String(placedHere);
        mc.classList.add("placed");
      } else {
        const notes = el("div", "mnotes");
        for (let d = 1; d <= 9; d++) {
          const s = el("span");
          if (hasDigit(ex.candidates[i], d)) {
            s.textContent = String(d);
            if (opts.crossed.has(i + ":" + d)) s.className = "cross";
            const cell = i;
            const digit = d;
            on(s, "click", () => opts.onCandidate(cell, digit, s));
          }
          notes.appendChild(s);
        }
        mc.appendChild(notes);
      }
      container.appendChild(mc);
    }
  }

  private renderMini(container: HTMLElement, ex: LessonExample, stage: number): void {
    container.innerHTML = "";
    const focus = new Set(ex.focusCells);
    const unitCells = new Set<number>();
    if (stage >= 1) for (const u of ex.units) for (const c of UNITS[u]) unitCells.add(c);
    const elimAt = new Map<number, Set<number>>();
    for (const e of ex.eliminations) {
      if (!elimAt.has(e.cell)) elimAt.set(e.cell, new Set());
      elimAt.get(e.cell)!.add(e.digit);
    }
    const placeAt = new Map<number, number>();
    for (const p of ex.placements) placeAt.set(p.cell, p.digit);

    for (let i = 0; i < 81; i++) {
      const mc = el("div", miniStaticClass(i));
      const value = ex.values[i];
      const placed = stage >= 2 ? placeAt.get(i) : undefined;

      if (stage >= 1 && unitCells.has(i) && !focus.has(i)) mc.classList.add("unit");
      if (stage >= 1 && focus.has(i)) mc.classList.add("focus");

      if (value) {
        mc.textContent = String(value);
      } else if (placed !== undefined) {
        mc.textContent = String(placed);
        mc.classList.add("placed");
      } else {
        const notes = el("div", "mnotes");
        const elims = elimAt.get(i);
        const isPatternDigit = (d: number) => ex.digits.includes(d) && (focus.has(i) || (stage >= 2 && elims?.has(d)));
        for (let d = 1; d <= 9; d++) {
          const s = el("span");
          if (hasDigit(ex.candidates[i], d)) {
            s.textContent = String(d);
            if (stage >= 2 && elims?.has(d)) s.className = "elim";
            else if (stage >= 1 && isPatternDigit(d)) s.className = "dig";
          }
          notes.appendChild(s);
        }
        mc.appendChild(notes);
      }
      container.appendChild(mc);
    }
  }
}
